import React, { useRef, useState, useEffect } from "react";
import { Outlet, useLocation, Link, useNavigate } from "react-router-dom";
import { useClickOutside } from "primereact/hooks";
import Header from "./header/Header.tsx";
import {
  LayoutDashboard,
  Video,
  DollarSign,
  Bell,
  Settings,
  ChevronDown,
  ChevronRight,
  Menu,
  X,
  Receipt,
  TrendingUp,
  UserCheck,
  Store,
  AlertTriangle,
  Lock,
} from "lucide-react";
import LiveHelpIcon from "@mui/icons-material/LiveHelp";
import MailIcon from "@mui/icons-material/Mail";
import DevicesRoundedIcon from "@mui/icons-material/DevicesRounded";
import LightTooltip from "./CustomTooltip.tsx";
import BrandLogo from "../assets/components/BrandLogo.tsx";

interface NavItem {
  label: string;
  icon: React.ReactNode;
  route?: string;
  platinum?: boolean;
  children?: NavItem[];
}

const navSections: { title: string; items: NavItem[] }[] = [
  {
    title: "Command Center",
    items: [
      {
        label: "Overview",
        icon: <LayoutDashboard size={18} />,
        route: "/home",
      },
      { label: "Live View", icon: <Video size={18} />, route: "/live" },
      {
        label: "Cashier Fraud",
        icon: <DollarSign size={18} />,
        route: "/cashier-fraud",
      },
      // { label: "POS Loss Prevention", icon: <DollarSign size={18} />, route: "/cashier-fraud" },
      // { label: "Event Search", icon: <Search size={18} />, route: "/event-search" },
      // { label: "Transactions", icon: <Receipt size={18} />, route: "/transactions" },
    ],
  },
  {
    title: "Analytics",
    items: [
      {
        label: "Reports",
        icon: <TrendingUp size={18} />,
        route: "/report-summary",
      },
      // { label: "Sales", icon: <TrendingUp size={18} />, route: "/report-summary" },
      // { label: "People", icon: <Users size={18} />, route: "/people-analytics" },
      // { label: "Cashier", icon: <UserCheck size={18} />, route: "/cashier-analytics" },
      // { label: "Register", icon: <CreditCard size={18} />, route: "/register-analytics" },
    ],
  },
  // {
  //   title: "AI Intelligence",
  //   items: [
  //     { label: "AI Detection Hub", icon: <Brain size={18} />, route: "/ai-detection" },
  //     { label: "Safety Events", icon: <ShieldAlert size={18} />, route: "/safety-events", platinum: true },
  //     { label: "Face & LPR", icon: <ScanFace size={18} />, route: "/face-lpr", platinum: true },
  //     { label: "Compliance AI", icon: <ClipboardCheck size={18} />, route: "/compliance-ai", platinum: true },
  //   ],
  // },
  {
    title: "Advanced",
    items: [
      {
        label: "Devices",
        icon: <DevicesRoundedIcon sx={{ width: "18px" }} />,
        route: "/devices",
      },
      // { label: "Multi-Store", icon: <Store size={18} />, route: "/multi-store", platinum: true },
      // { label: "Inventory Analytics", icon: <Package size={18} />, route: "/inventory-analytics", platinum: true },
    ],
  },
  {
    title: "Operations",
    items: [
      {
        label: "Client Queries",
        icon: <AlertTriangle size={18} />,
        route: "/client-query",
      },
      // { label: "Incident Center", icon: <AlertTriangle size={18} />, route: "/client-query" },
      // { label: "Reports", icon: <FileText size={18} />, route: "/reports" },
      {
        label: "Alerts & Rules",
        icon: <Bell size={18} />,
        children: [
          {
            label: "Transaction Alerts",
            icon: <Receipt size={16} />,
            route: "/transaction-alert",
          },
          {
            label: "Trigger Alerts",
            icon: <AlertTriangle size={16} />,
            route: "/trigger-alerts",
          },
        ],
      },
      {
        label: "Settings",
        icon: <Settings size={18} />,
        children: [
          {
            label: "Profile",
            icon: <UserCheck size={16} />,
            route: "/profile",
          },
          {
            label: "Change Password",
            icon: <Lock size={16} />,
            route: "/change-pwd",
          },
          {
            label: "Manage Store",
            icon: <Store size={16} />,
            route: "/manage-stores",
          },
        ],
      },
      {
        label: "Contact Us",
        icon: <MailIcon sx={{ width: "18px" }} />,
        route: "/contact-us",
      },
      {
        label: "FAQ",
        icon: <LiveHelpIcon sx={{ width: "18px" }} />,
        route: "/faq",
      },
    ],
  },
];

export default function MiniDrawer() {
  const location = useLocation();
  const navigate = useNavigate();
  const sidebarRef = useRef(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [darkMode, setDarkMode] = useState<boolean>(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("theme") === "dark";
    }
    return false;
  });

  useEffect(() => {
    const root = document.documentElement;
    if (darkMode) {
      root.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      root.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  }, [darkMode]);

  const toggleDarkMode = () => setDarkMode((prev: boolean) => !prev);

  const toggleExpand = (label: string) => {
    setExpandedItems((prev: string[]) =>
      prev.includes(label)
        ? prev.filter((l: string) => l !== label)
        : [...prev, label],
    );
  };

  const isActive = (route?: string) => {
    if (!route) return false;
    return location.pathname === route;
  };

  const isChildActive = (children?: NavItem[]) => {
    if (!children) return false;
    return children.some((child) => child.route === location.pathname);
  };

  useClickOutside(sidebarRef, () => {
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  });

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Mobile overlay — fade in/out smoothly */}
      <div
        className={`
          fixed inset-0 bg-black/40 z-20 lg:hidden
          transition-opacity duration-300 ease-in-out
          ${sidebarOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}
        `}
        onClick={() => setSidebarOpen(false)}
      />

      {/* Sidebar */}
      <aside
        ref={sidebarRef}
        className={`
          fixed top-0 left-0 z-30 h-full flex flex-col
          bg-[rgb(var(--sidebar-bg))] text-[rgb(var(--sidebar-text))]
          border-r border-[rgb(var(--sidebar-border))]
          transition-[width,transform] duration-300 ease-in-out
          ${sidebarOpen ? "w-[240px]" : "w-[60px] -translate-x-full md:translate-x-0"}
          lg:relative
        `}
      >
        {/* Sidebar Header */}
        <div
          className={`
            flex items-center px-3
            border-b border-[rgb(var(--sidebar-border))] shrink-0
            ${sidebarOpen ? "justify-between" : "justify-center"}
          `}
        >
          {/* Logo wrapper: always rendered, animated in/out via opacity + max-width */}
          <div
            className={`
              overflow-hidden
              transition-[max-width,opacity] duration-300 ease-in-out
              ${sidebarOpen ? "max-w-[180px] opacity-100" : "max-w-0 opacity-0"}
            `}
          >
            <Link
              rel="noopener noreferrer"
              style={{ textDecoration: "none" }}
              to="/home"
              className="flex justify-center"
            >
              <div className="py-3 whitespace-nowrap">
                <BrandLogo isDark={darkMode} />
              </div>
            </Link>
          </div>

          {/* Toggle button */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="my-4 p-1.5 rounded-md hover:bg-[rgb(var(--sidebar-text)/0.08)] text-[rgb(var(--sidebar-text)/0.6)] hover:text-[rgb(var(--sidebar-text))] transition-colors shrink-0"
          >
            {/* Animate the icon swap */}
            <span
              className={`
                block transition-[transform,opacity] duration-200
                ${sidebarOpen ? "opacity-100 rotate-0" : "opacity-0 -rotate-90 absolute"}
              `}
            >
              <X size={16} />
            </span>
            <span
              className={`
                block transition-[transform,opacity] duration-200
                ${!sidebarOpen ? "opacity-100 rotate-0" : "opacity-0 rotate-90 absolute"}
              `}
            >
              <Menu size={16} />
            </span>
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden py-3 px-2 space-y-4 scrollbar-thin">
          {navSections.map((section) => (
            <div key={section.title}>
              {/* Section label: always rendered, animated */}
              <div
                className={`
                  overflow-hidden
                  transition-[max-height,opacity,margin] duration-300 ease-in-out
                  ${sidebarOpen ? "max-h-8 opacity-100 mb-1.5" : "max-h-0 opacity-0 mb-0"}
                `}
              >
                <p className="nav-section-label px-2 whitespace-nowrap">
                  {section.title}
                </p>
              </div>

              <ul className="space-y-0.5">
                {section.items.map((item) => (
                  <NavItemComponent
                    key={item.label}
                    item={item}
                    sidebarOpen={sidebarOpen}
                    isActive={isActive}
                    isChildActive={isChildActive}
                    expandedItems={expandedItems}
                    toggleExpand={toggleExpand}
                  />
                ))}
              </ul>
            </div>
          ))}
        </nav>

        {/* Sidebar Footer */}
        {/* <div className="shrink-0 border-t border-white/[0.06] p-2">
          {sidebarOpen ? (
            <p className="text-caption text-white/20 text-center">v1.0 · 2026</p>
          ) : null}
        </div> */}
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-auto px-4 md:px-10 pb-10">
        {/* Mobile menu button */}
        <button
          className="md:hidden fixed top-6 right-6 z-40 p-2 rounded-md bg-[rgb(var(--surface-2))] border border-[rgb(var(--border-primary))] shadow-sm"
          onClick={() => setSidebarOpen(true)}
          style={{ display: sidebarOpen ? "none" : "" }}
        >
          <Menu size={18} className="text-[rgb(var(--text-primary))]" />
        </button>

        {/* Header */}
        <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

        {/* Page Content */}
        <Outlet />
      </div>
    </div>
  );
}

/* ─── Nav Item Component ─── */
function NavItemComponent({
  item,
  sidebarOpen,
  isActive,
  isChildActive,
  expandedItems,
  toggleExpand,
}: {
  item: NavItem;
  sidebarOpen: boolean;
  isActive: (route?: string) => boolean;
  isChildActive: (children?: NavItem[]) => boolean;
  expandedItems: string[];
  toggleExpand: (label: string) => void;
}) {
  const hasChildren = item.children && item.children.length > 0;
  const expanded = expandedItems.includes(item.label);
  const active = isActive(item.route);
  const childActive = isChildActive(item.children);

  if (hasChildren) {
    const buttonEl = (
      <button
        onClick={() => toggleExpand(item.label)}
        className={`
          w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm
          transition-colors duration-150
          ${
            childActive
              ? "bg-gold-2/10 text-gold-2 dark:text-[rgb(var(--sidebar-text))]"
              : "text-text-secondary hover:text-[rgb(var(--sidebar-text))] hover:bg-[rgb(var(--sidebar-text)/0.05)]"
          }
        `}
      >
        <span className={`shrink-0 ${active ? "text-gold-2" : ""}`}>
          {item.icon}
        </span>

        {/* Label: always rendered, animated */}
        <span
          className={`
            flex-1 text-left overflow-hidden
            transition-[max-width,opacity] duration-300 ease-in-out
            ${sidebarOpen ? "max-w-full opacity-100" : "max-w-0 opacity-0"}
          `}
        >
          <span className="truncate nav-item-text whitespace-nowrap block">
            {item.label}
          </span>
        </span>

        {/* Chevron: always rendered, animated */}
        <span
          className={`
            shrink-0
            transition-[opacity,width,transform] duration-300 ease-in-out
            ${sidebarOpen ? "opacity-100 w-4" : "opacity-0 w-0"}
          `}
        >
          <span
            className={`block transition-transform duration-200 ${expanded ? "rotate-0" : "-rotate-90"}`}
          >
            <ChevronDown size={14} />
          </span>
        </span>
      </button>
    );

    return (
      <li>
        {!sidebarOpen ? (
          <LightTooltip title={item.label} placement="right-end" arrow>
            {buttonEl}
          </LightTooltip>
        ) : (
          buttonEl
        )}

        {/* Children dropdown: animated via max-height */}
        <div
          className={`
            overflow-hidden
            transition-[max-height,opacity] duration-300 ease-in-out
            ${sidebarOpen && expanded ? "max-h-96 opacity-100" : "max-h-0 opacity-0"}
          `}
        >
          <ul className="ml-4 mt-0.5 space-y-0.5 border-l border-[rgb(var(--sidebar-border))] pl-2.5">
            {item.children!.map((child) => (
              <li key={child.label}>
                <Link
                  to={child.route || "#"}
                  className={`
                    flex items-center gap-2 px-2 py-1.5 rounded-md text-[12px]
                    transition-colors duration-150
                    ${
                      isActive(child.route)
                        ? "bg-gold-2/10 text-gold-2 font-medium"
                        : "text-[rgb(var(--sidebar-text)/0.5)] hover:text-[rgb(var(--sidebar-text))] hover:bg-[rgb(var(--sidebar-text)/0.05)]"
                    }
                  `}
                >
                  <span className="shrink-0">{child.icon}</span>
                  <span className="truncate">{child.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </li>
    );
  }

  const linkEl = (
    <Link
      to={item.platinum ? "#" : item.route || "#"}
      className={`
        flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm
        transition-colors duration-150 relative
        ${
          active
            ? "bg-gold-2/10 text-gold-2 dark:text-[rgb(var(--sidebar-text))] font-medium border-l-[3px] border-gold-2 pl-2"
            : "text-text-secondary hover:text-[rgb(var(--sidebar-text))] hover:bg-[rgb(var(--sidebar-text)/0.05)]"
        }
        ${item.platinum ? "opacity-50 cursor-not-allowed" : ""}
      `}
      onClick={(e: React.MouseEvent) => {
        if (item.platinum) e.preventDefault();
      }}
    >
      <span className={`shrink-0 ${active ? "text-gold-2" : ""}`}>
        {item.icon}
      </span>

      {/* Label + badge: always rendered, animated */}
      <span
        className={`
          flex items-center gap-1.5 flex-1 overflow-hidden
          transition-[max-width,opacity] duration-300 ease-in-out
          ${sidebarOpen ? "max-w-full opacity-100" : "max-w-0 opacity-0"}
        `}
      >
        <span className="flex-1 truncate nav-item-text whitespace-nowrap">
          {item.label}
        </span>
        {item.platinum && (
          <span className="nav-badge bg-gold-2/20 text-gold-1 px-1.5 py-0.5 rounded whitespace-nowrap">
            Platinum
          </span>
        )}
      </span>
    </Link>
  );

  return (
    <li>
      {!sidebarOpen ? (
        <LightTooltip title={item.label} placement="right-end" arrow>
          {linkEl}
        </LightTooltip>
      ) : (
        linkEl
      )}
    </li>
  );
}